'use client';

import { useState, useEffect } from "react";
import styles from "./ContactForm.module.css";

/**
 * ContactForm Component
 * ---------------------
 * Displays a modal contact form allowing users to contact a photographer.
 * 
 * Props:
 *  - name (string): the photographer’s name (displayed in the modal title)
 *  - isOpen (boolean): determines whether the modal is visible
 *  - onClose (function): callback function to close the modal
 */
export default function ContactForm({ name, isOpen, onClose }) {
  // State to store the form field values
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });

  /**
   * Effect: close modal when "Escape" key is pressed.
   * Adds a global keydown listener when the component mounts,
   * and removes it when it unmounts to prevent memory leaks.
   */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // If the modal is closed, render nothing
  if (!isOpen) return null;

  /**
   * Updates the formData state when user types in an input field.
   * Uses computed property names to handle all fields dynamically.
   */
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Handles form submission:
   *  - Prevents page reload.
   *  - Validates required fields and email format.
   *  - Logs form data to the console (could later be sent to a server).
   *  - Clears the form and closes the modal after successful submission.
   */
  const handleSubmit = (event) => {
    event.preventDefault();

    // Simple email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Basic form validation
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.message ||
      !emailRegex.test(formData.email)
    ) {
      alert("Veuillez remplir tous les champs avant de soumettre.");
      return;
    }

    // Simulate form submission (for now, just log the values)
    console.log("Formulaire soumis:");
    console.log("Prénom:", formData.firstName);
    console.log("Nom:", formData.lastName);
    console.log("Email:", formData.email);
    console.log("Message:", formData.message);

    // Reset the form and close the modal
    setFormData({ firstName: "", lastName: "", email: "", message: "" });
    onClose();
  };

  return (
    // Overlay wrapper — clicking outside the modal closes it
    <div className={styles.modalWrapper} onClick={onClose}>
      {/* Modal content — stop click events from bubbling to overlay */}
      <div
        className={styles.modal}
        id="contactForm"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Close button (X icon) */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 384 512"
          className={styles.close}
          onClick={onClose}
          fill="white"
        >
          <path d="M55.1 73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L147.2 256 9.9 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192.5 301.3 329.9 438.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.8 256 375.1 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192.5 210.7 55.1 73.4z" />
        </svg>

        {/* Modal Title with photographer’s name */}
        <h1 className={styles.contactTitle}>
          Contactez-moi
          <br />
          {name}
        </h1>

        {/* Contact Form */}
        <form className={styles.form} onSubmit={handleSubmit}>
          {/* First Name Field */}
          <label htmlFor="firstName">Prénom</label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
          />

          {/* Last Name Field */}
          <label htmlFor="lastName">Nom</label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
          />

          {/* Email Field */}
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />

          {/* Message Field */}
          <label htmlFor="message">Votre message</label>
          <textarea
            id="message"
            name="message"
            rows="8"
            value={formData.message}
            onChange={handleChange}
            required
          ></textarea>

          {/* Submit Button */}
          <button type="submit" className={styles.submitButton}>
            Envoyer
          </button>
        </form>
      </div>
    </div>
  );
}
